import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import './MembershipWidget.css';

/**
 * MembershipWidget - Standalone widget page for embedding in external websites via iframe.
 *
 * Mirrors the apply flow from src/pages/Membership.jsx but without the site Navbar/Footer
 * so it fits cleanly inside an iframe. Auth is provided by the host through query params
 * (token) or, when loaded directly on this origin, falls back to localStorage.
 *
 * Query params:
 * - token            string  - JWT used for the Authorization header. Falls back to
 *                              localStorage.getItem('tali_client_token') if absent.
 * - primaryColor     string  - hex, themes the widget
 * - secondaryColor   string
 * - accentColor      string
 * - textColor        string
 * - backgroundColor  string
 *
 * On a successful submit the iframe navigates the top frame to the Paymob URL
 * (window.top.location). It also posts a "tali-membership" message to the parent
 * frame for events the host may want to handle (e.g. auth-expired -> redirect to login,
 * resize -> adjust iframe height to fit content).
 */
const BASE_API = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

const post = (msg) => {
  try {
    window.parent && window.parent.postMessage({ source: 'tali-membership', ...msg }, '*');
  } catch (e) {
    /* noop */
  }
};

const goTo = (url) => {
  try {
    window.top.location.href = url;
  } catch (e) {
    window.location.href = url;
  }
};

const normalizePhone = (p) => {
  const digits = (p || '').replace(/[^\d]/g, '');
  if (!digits) return '';
  // Egyptian local format (e.g. 01226294984) -> international (201226294984)
  if (digits.startsWith('0')) return '20' + digits.slice(1);
  return digits;
};
const fullName = (u) => [u?.firstName, u?.lastName].filter(Boolean).join(' ').trim();
const toDateInput = (iso) => (iso ? String(iso).slice(0, 10) : '');
const MIN_AGE_YEARS = 23;
const maxBirthdate = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - MIN_AGE_YEARS);
  return d.toISOString().slice(0, 10);
};
const isValidBirthdate = (v) => !v || v <= maxBirthdate();
const SOCIAL_LINK_REGEX = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+(\/[^\s]*)?$/;
const isValidSocialLink = (v) => !v || SOCIAL_LINK_REGEX.test(v.trim());
const pickNonEmpty = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ''));

// ---------- field validators ----------
// Letters (any language), spaces, hyphens, apostrophes and dots only.
const NAME_REGEX = /^[\p{L}][\p{L} .'-]*$/u;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const nameError = (v, label = 'Full name') => {
  const val = (v || '').trim();
  if (!val) return `${label} is required.`;
  if (val.length < 3) return `${label} must be at least 3 characters.`;
  if (val.length > 60) return `${label} must be 60 characters or fewer.`;
  if (!NAME_REGEX.test(val)) return `${label} can only contain letters, spaces, hyphens and apostrophes.`;
  return '';
};
const emailError = (v) => {
  const val = (v || '').trim();
  if (!val) return 'Email is required.';
  if (!EMAIL_REGEX.test(val)) return 'Please enter a valid email address (e.g. name@example.com).';
  return '';
};
const phoneError = (v, label = 'Phone number') => {
  const digits = (v || '').replace(/\D/g, '');
  if (!digits) return `${label} is required.`;
  if (digits.length < 8 || digits.length > 15) return `Please enter a valid ${label.toLowerCase()}.`;
  return '';
};
const socialError = (v, label = 'Social media link', example = 'https://instagram.com/yourhandle') => {
  const val = (v || '').trim();
  if (!val) return `${label} is required.`;
  if (!isValidSocialLink(val)) return `Please enter a valid link (e.g. ${example}).`;
  return '';
};

// Photo upload constraints — must match backend membership-photo filter / limits.
const ALLOWED_PHOTO_MIMES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_PHOTO_BYTES = 5 * 1024 * 1024; // 5 MB
const photoFileError = (file, label = 'Photo') => {
  if (!file) return `${label} is required.`;
  if (!ALLOWED_PHOTO_MIMES.includes(file.type)) {
    return `${label} must be a JPEG, PNG or WebP image.`;
  }
  if (file.size > MAX_PHOTO_BYTES) return `${label} must be 5 MB or smaller.`;
  return '';
};

// Order used to scroll to the first field with an error.
const FIELD_ORDER = [
  'name', 'email', 'phone', 'socialMediaLink', 'birthdate', 'photo',
  'coupleName', 'couplePhone', 'coupleEmail', 'coupleSocialMediaLink', 'couplePhoto',
];

const MembershipWidget = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || localStorage.getItem('tali_client_token');

  const themeStyles = {
    '--mw-primary': searchParams.get('primaryColor') || '#C2410C',
    '--mw-secondary': searchParams.get('secondaryColor') || '#F4D9C8',
    '--mw-accent': searchParams.get('accentColor') || '#F2C744',
    '--mw-text': searchParams.get('textColor') || '#111111',
    '--mw-bg': searchParams.get('backgroundColor') || '#FFF6E5',
  };

  const authHeaders = () => (token ? { Authorization: `Bearer ${token}` } : {});

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [authExpired, setAuthExpired] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [couplePhotoFile, setCouplePhotoFile] = useState(null);
  const [couplePhotoPreview, setCouplePhotoPreview] = useState('');

  const [formData, setFormData] = useState({
    type: 1,
    authCapture: true,
    name: '',
    phone: '',
    email: '',
    socialMediaLink: '',
    addressStreet: '',
    addressNeighborhood: '',
    addressMoreInfo: '',
    residenceCountry: '',
    birthdate: '',
    gender: 1,
    coupleName: '',
    couplePhone: '',
    coupleEmail: '',
    coupleSocialMediaLink: '',
  });

  // ---------- data loading ----------
  useEffect(() => {
    if (!token) {
      setAuthExpired(true);
      setLoading(false);
      post({ type: 'auth-required' });
      return;
    }
    const load = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${BASE_API}/memberships/my-status`, { headers: authHeaders() });
        const data = res.data?.data ?? res.data;
        setMembershipStatus(data);
        setShowForm(data?.canApply === true || (data?.status === 'none' && !data?.hasMembership));
      } catch (err) {
        if (err?.response?.status === 401) {
          setAuthExpired(true);
          post({ type: 'auth-expired' });
          return;
        }
        // Same fallback Membership.jsx uses: show the form anyway so user can try to apply.
        setError('Failed to load membership status');
        setShowForm(true);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Public pricing endpoint — no auth required.
  useEffect(() => {
    axios
      .get(`${BASE_API}/memberships/pricing`)
      .then((r) => setPricing(r.data?.data ?? r.data))
      .catch(() =>
        setPricing({
          single: { priceUsd: 150, duration: 12 },
          couple: { priceUsd: 250, duration: 12 },
        }),
      );
  }, []);

  // Prefill name/email/phone from the signed-in user. The widget has no
  // AuthContext (it can be embedded cross-origin where the parent's localStorage
  // is invisible), so fetch the profile from the API instead of reading any
  // cached user object. The /users/profile/me response wraps the user record:
  //   { user: { firstName, lastName, email, phoneNumber }, stats, ... }
  // — so we unwrap data.user before mapping fields. pickNonEmpty strips
  // undefined/empty values, so spreading fromUser over prev never wipes out
  // anything the user has already typed.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios
      .get(`${BASE_API}/users/profile/me`, { headers: authHeaders() })
      .then((r) => {
        if (cancelled) return;
        const body = r.data?.data ?? r.data;
        const u = body?.user ?? body;
        if (!u) return;
        const fromUser = pickNonEmpty({
          name: fullName(u),
          email: u.email,
          phone: normalizePhone(u.phoneNumber || u.phone),
        });
        setFormData((prev) => ({ ...prev, ...fromUser }));
      })
      .catch(() => {
        // Best-effort prefill — silent on failure (form still works manually).
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Prefill form from existing membership (handles apply-again after rejected/expired).
  useEffect(() => {
    const m = membershipStatus?.membership;
    if (!m) return;
    const fromMembership = pickNonEmpty({
      type: m.type != null ? Number(m.type) : undefined,
      name: m.name,
      email: m.email,
      phone: normalizePhone(m.phone),
      socialMediaLink: m.socialMediaLink,
      birthdate: toDateInput(m.birthdate),
      gender: m.gender != null ? Number(m.gender) : undefined,
      addressStreet: m.addressStreet,
      addressNeighborhood: m.addressNeighborhood,
      addressMoreInfo: m.addressMoreInfo,
      residenceCountry: m.residenceCountry,
      coupleName: m.coupleName,
      couplePhone: normalizePhone(m.couplePhone),
      coupleEmail: m.coupleEmail,
      coupleSocialMediaLink: m.coupleSocialMediaLink,
    });
    setFormData((prev) => ({ ...prev, ...fromMembership }));
  }, [membershipStatus]);

  // Auto-resize: tell the host iframe how tall the content is.
  // Measures the inner wrapper (the actual layout box) instead of body/html, and
  // only posts when the height actually changes — otherwise the host's per-post
  // padding buffer turns into a slow-growth feedback loop.
  useEffect(() => {
    if (typeof window === 'undefined' || !('ResizeObserver' in window)) return;
    const target = document.querySelector('.membership-widget-wrapper');
    if (!target) return;
    let lastSent = 0;
    const sendHeight = () => {
      const h = Math.ceil(target.getBoundingClientRect().height);
      if (h > 0 && Math.abs(h - lastSent) > 2) {
        lastSent = h;
        post({ type: 'resize', height: h });
      }
    };
    const ro = new ResizeObserver(sendHeight);
    ro.observe(target);
    sendHeight();
    return () => ro.disconnect();
  }, [loading, membershipStatus, showForm, authExpired, error]);

  // ---------- handlers ----------
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear this field's error as soon as the user starts correcting it.
    setFieldErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev));
  };

  const handlePhoneChange = (value, field) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));
  };

  const handlePhotoChange = (e, field) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const label = field === 'couplePhoto' ? "Partner's photo" : 'Photo';
    const err = photoFileError(file, label);
    if (err) {
      setFieldErrors((prev) => ({ ...prev, [field]: err }));
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      if (field === 'couplePhoto') {
        setCouplePhotoFile(file);
        setCouplePhotoPreview(reader.result);
      } else {
        setPhotoFile(file);
        setPhotoPreview(reader.result);
      }
    };
    reader.readAsDataURL(file);
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));
  };

  const handleRemovePhoto = (field) => {
    if (field === 'couplePhoto') {
      setCouplePhotoFile(null);
      setCouplePhotoPreview('');
    } else {
      setPhotoFile(null);
      setPhotoPreview('');
    }
  };

  // Validate every required field and return a { fieldName: message } map.
  const validateForm = () => {
    const errs = {
      name: nameError(formData.name),
      email: emailError(formData.email),
      phone: phoneError(formData.phone),
      socialMediaLink: socialError(formData.socialMediaLink),
      birthdate:
        formData.birthdate && !isValidBirthdate(formData.birthdate)
          ? `You must be at least ${MIN_AGE_YEARS} years old.`
          : '',
      photo: photoFileError(photoFile),
    };
    if (formData.type === 2) {
      errs.coupleName = nameError(formData.coupleName, "Partner's full name");
      errs.couplePhone = phoneError(formData.couplePhone, "Partner's phone");
      errs.coupleEmail = emailError(formData.coupleEmail);
      errs.coupleSocialMediaLink = socialError(
        formData.coupleSocialMediaLink,
        "Partner's social media link",
        'https://instagram.com/partnerhandle',
      );
      errs.couplePhoto = photoFileError(couplePhotoFile, "Partner's photo");
    }
    return Object.fromEntries(Object.entries(errs).filter(([, v]) => v));
  };

  // Validate a single field on blur so users get feedback as they go.
  const validateSingle = (field) => {
    const errs = validateForm();
    setFieldErrors((prev) => ({ ...prev, [field]: errs[field] || '' }));
  };

  // Scroll to (and focus) the first field with an error. Inside an iframe the
  // host controls the real scrollbar, so we also post the offset to the parent.
  const scrollToFirstError = (errs) => {
    const firstKey = FIELD_ORDER.find((k) => errs[k]);
    if (!firstKey) return;
    const el = document.getElementById(`mw-field-${firstKey}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const input = el.querySelector('input, select, textarea');
    if (input) window.setTimeout(() => input.focus({ preventScroll: true }), 350);
    const wrapper = document.querySelector('.membership-widget-wrapper');
    const top = wrapper
      ? el.getBoundingClientRect().top - wrapper.getBoundingClientRect().top
      : el.offsetTop;
    post({ type: 'scroll-to-error', top: Math.max(0, Math.floor(top)) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Field-level validation
    const errs = validateForm();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      scrollToFirstError(errs);
      return;
    }
    setFieldErrors({});

    try {
      setSubmitting(true);
      const submitData = { ...formData };
      if (!submitData.birthdate) delete submitData.birthdate;
      if (!submitData.addressStreet) delete submitData.addressStreet;
      if (!submitData.addressNeighborhood) delete submitData.addressNeighborhood;
      if (!submitData.addressMoreInfo) delete submitData.addressMoreInfo;
      if (!submitData.residenceCountry) delete submitData.residenceCountry;
      if (!submitData.coupleSocialMediaLink) delete submitData.coupleSocialMediaLink;
      if (submitData.type !== 2) {
        delete submitData.coupleName;
        delete submitData.couplePhone;
        delete submitData.coupleEmail;
        delete submitData.coupleSocialMediaLink;
      }

      const fd = new FormData();
      Object.entries(submitData).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') fd.append(k, v);
      });
      fd.append('photo', photoFile);
      if (formData.type === 2 && couplePhotoFile) {
        fd.append('couplePhoto', couplePhotoFile);
      }

      const res = await axios.post(`${BASE_API}/memberships/apply`, fd, {
        headers: authHeaders(),
      });
      const data = res.data?.data ?? res.data;
      post({ type: 'submitted', data });
      if (data?.paymentUrl) {
        goTo(data.paymentUrl);
        return;
      }
      setError('Payment initialization failed. Please try again or contact support.');
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthExpired(true);
        post({ type: 'auth-expired' });
        return;
      }
      let msg = 'Failed to submit application';
      const data = err?.response?.data;
      if (data) {
        if (Array.isArray(data.message)) {
          msg = (
            <ul className="mb-0">
              {data.message.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          );
        } else if (typeof data.message === 'string') msg = data.message;
        else if (typeof data.error === 'string') msg = data.error;
      } else if (err.message) msg = err.message;
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePay = async () => {
    try {
      setSubmitting(true);
      const id = membershipStatus?.membership?.id;
      if (!id) return;
      const res = await axios.get(`${BASE_API}/memberships/${id}/payment`, { headers: authHeaders() });
      const data = res.data?.data ?? res.data;
      if (data?.paymentUrl) goTo(data.paymentUrl);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthExpired(true);
        post({ type: 'auth-expired' });
        return;
      }
      setError(err?.response?.data?.message || 'Failed to initialize payment');
      setSubmitting(false);
    }
  };

  const triggerBlobDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadQR = async (id) => {
    try {
      const res = await axios.get(`${BASE_API}/memberships/${id}/qr-code`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      triggerBlobDownload(new Blob([res.data], { type: 'image/png' }), 'membership-qr.png');
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthExpired(true);
        post({ type: 'auth-expired' });
        return;
      }
      setError('Failed to download QR code. Please try again.');
    }
  };

  const handleDownloadAppleWallet = async (id) => {
    try {
      const res = await axios.get(`${BASE_API}/memberships/${id}/apple-wallet`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      triggerBlobDownload(
        new Blob([res.data], { type: 'application/vnd.apple.pkpass' }),
        'membership.pkpass',
      );
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthExpired(true);
        post({ type: 'auth-expired' });
        return;
      }
      if (err?.response?.status === 500) {
        setError('Apple Wallet is not configured. Please contact support.');
      } else {
        setError('Failed to add to Apple Wallet. Please try again.');
      }
    }
  };

  // ---------- render helpers ----------
  const renderMembershipCard = () => {
    const m = membershipStatus?.membership;
    if (!m) return null;
    const daysRemaining = m.daysRemaining || 0;
    const isPendingCapture = membershipStatus?.status === 'pending_capture';
    const displayStatusName = isPendingCapture ? 'Pending Confirmation' : (m.statusName || m.status);
    const badgeClass = isPendingCapture ? 'mw-badge mw-badge-pending-capture' : 'mw-badge';
    return (
      <div className="mw-card">
        <div className="mw-card-head">
          <div>
            <h2>{m.typeName || 'Membership'} Membership</h2>
            <span className={badgeClass}>{displayStatusName}</span>
          </div>
          {m.code && (
            <div className="mw-code">
              <div className="mw-code-label">Member Code</div>
              <div className="mw-code-value">{m.code}</div>
            </div>
          )}
        </div>
        <div className="mw-card-body">
          <div className="mw-member-block">
            <div className="mw-member-heading">
              <h3><i className="bi bi-person" aria-hidden="true"></i> Member</h3>
              {m.photo && <img src={m.photo} alt={m.name} className="mw-member-photo" />}
            </div>
            <div className="mw-info-row"><span className="mw-info-label">Name</span><span>{m.name}</span></div>
            <div className="mw-info-row"><span className="mw-info-label">Email</span><span>{m.email}</span></div>
            <div className="mw-info-row"><span className="mw-info-label">Phone</span><span>{m.phone}</span></div>
            {m.socialMediaLink && (
              <div className="mw-info-row">
                <span className="mw-info-label">Social</span>
                <span>
                  <a href={m.socialMediaLink} target="_blank" rel="noopener noreferrer">
                    {m.socialMediaLink}
                  </a>
                </span>
              </div>
            )}
          </div>

          {String(m.type) === '2' && (
            <div className="mw-member-block mw-partner-block">
              <div className="mw-member-heading">
                <h3><i className="bi bi-people" aria-hidden="true"></i> Partner</h3>
                {m.couplePhoto && (
                  <img src={m.couplePhoto} alt={m.coupleName || 'Partner'} className="mw-member-photo" />
                )}
              </div>
              <div className="mw-info-row">
                <span className="mw-info-label">Name</span>
                <span>{m.coupleName || '—'}</span>
              </div>
              {m.coupleEmail && (
                <div className="mw-info-row"><span className="mw-info-label">Email</span><span>{m.coupleEmail}</span></div>
              )}
              {m.couplePhone && (
                <div className="mw-info-row"><span className="mw-info-label">Phone</span><span>{m.couplePhone}</span></div>
              )}
              {m.coupleSocialMediaLink && (
                <div className="mw-info-row">
                  <span className="mw-info-label">Social</span>
                  <span>
                    <a href={m.coupleSocialMediaLink} target="_blank" rel="noopener noreferrer">
                      {m.coupleSocialMediaLink}
                    </a>
                  </span>
                </div>
              )}
            </div>
          )}
          {m.startDate && m.endDate && (
            <div className="mw-info-row">
              <span className="mw-info-label">Valid</span>
              <span>
                {new Date(m.startDate).toLocaleDateString()} — {new Date(m.endDate).toLocaleDateString()}
                {' · '}
                <span className={daysRemaining > 0 && daysRemaining < 30 ? 'mw-days-warning' : ''}>
                  {daysRemaining > 0 ? `${daysRemaining} days remaining` : 'expired'}
                </span>
              </span>
            </div>
          )}
        </div>

        {m.qrCode && (
          <div className="mw-qr-section">
            <div className="mw-qr-label">Scan QR Code</div>
            <img src={m.qrCode} alt="Membership QR Code" className="mw-qr-image" />
            <p className="mw-qr-description">Scan this code to verify your membership</p>
            <div className="mw-wallet-actions">
              <button
                type="button"
                className="mw-btn mw-btn-apple"
                onClick={() => handleDownloadAppleWallet(m.id)}
                title="Add to Apple Wallet"
              >
                <i className="bi bi-apple" aria-hidden="true"></i>
                <span>Add to Apple Wallet</span>
              </button>
              <button
                type="button"
                className="mw-btn mw-btn-outline"
                onClick={() => handleDownloadQR(m.id)}
                title="Download QR Code"
              >
                <i className="bi bi-download" aria-hidden="true"></i>
                <span>Download QR</span>
              </button>
            </div>
          </div>
        )}

        <ul className="mw-benefits">
          <li>Priority reservations</li>
          <li>Exclusive member events</li>
          <li>Special discounts</li>
          <li>Personalized experiences</li>
        </ul>
      </div>
    );
  };

  const renderStatusMessage = () => {
    const s = membershipStatus?.status;
    if (s === 'pending') {
      return (
        <div className="mw-alert mw-alert-info">
          <strong>Application Under Review.</strong>
          <p>Your application is being reviewed. You'll get an email once it's processed.</p>
        </div>
      );
    }
    if (s === 'approved') {
      return (
        <div className="mw-alert mw-alert-success">
          <strong>Approved!</strong>
          <p>Complete your payment to activate your membership.</p>
          <button className="mw-btn" onClick={handlePay} disabled={submitting}>
            {submitting ? 'Processing…' : 'Pay Now'}
          </button>
        </div>
      );
    }
    if (s === 'pending_payment') {
      return (
        <div className="mw-alert mw-alert-warning">
          <strong>Payment Pending.</strong>
          <p>Your payment is being processed. Membership activates once approved.</p>
        </div>
      );
    }
    if (s === 'pending_capture') {
      return (
        <div className="mw-alert mw-alert-warning">
          <strong>Payment Authorized — Awaiting Confirmation.</strong>
          <p>
            Your payment was authorized successfully. Our team will confirm your membership shortly —
            you will receive an email once it is active.
          </p>
        </div>
      );
    }
    if (s === 'rejected') {
      return (
        <div className="mw-alert mw-alert-danger">
          <strong>Application Not Approved.</strong>
          <p>You can apply again with updated information.</p>
          <button className="mw-btn" onClick={() => setShowForm(true)}>Apply Again</button>
        </div>
      );
    }
    if (s === 'expired') {
      return (
        <div className="mw-alert mw-alert-secondary">
          <strong>Membership Expired.</strong>
          <p>You can apply for a new membership below.</p>
          <button className="mw-btn" onClick={() => setShowForm(true)}>Apply Again</button>
        </div>
      );
    }
    return null;
  };

  const renderInfoSections = () => {
    // Admin-managed rich-text content for the currently selected type takes
    // precedence over the built-in default.
    const activeInfoContent =
      formData.type === 2 ? pricing?.infoContentCouple : pricing?.infoContentSingle;
    if (activeInfoContent) {
      return (
        <div
          className="mw-info-block mw-info-rich"
          dangerouslySetInnerHTML={{ __html: activeInfoContent }}
        />
      );
    }
    return (
    <>
      <section className="mw-info-block">
        <h2>Membership Benefits</h2>
        <ul>
          <li>Free entrance to all the parties throughout the summer</li>
          <li>10 free bouchons to use anytime during the summer</li>
          <li>Daily access to the members only beach area</li>
          <li>Minimum-spending does not apply to members</li>
          <li>Dedicated VIP phone number for reservations</li>
          <li>Skip the line! VIP private entrance into KIKI'S</li>
          <li>Members can book the beach for non-members (Selection and day-use fees apply)</li>
          <li>Members can book parties for non-members (Selection and ticket fees apply)</li>
        </ul>
      </section>

      <section className="mw-info-block">
        <h2>Kids Regulation</h2>
        <ul>
          <li>Kids are welcomed all week for breakfast and on beach until 6PM</li>
          <li>Members are allowed to get their kids (under 12 YO) to the members only areas for free (no need to pay for their minimum spend)</li>
          <li>Outside food or drinks are not allowed</li>
          <li>Nannies are not allowed</li>
        </ul>
      </section>

      <section className="mw-info-block mw-info-note">
        <h2>Additional Note</h2>
        <p>
          "Please note that the payment is done in dollars. If you face any issue,
          kindly contact us on <a href="mailto:hello@kikisexperience.com">hello@kikisexperience.com</a>"
        </p>
      </section>
    </>
    );
  };

  const renderTypeSelector = () => (
    <section className="mw-type-selector">
      <h3>Membership Type</h3>
      <div className="mw-types">
        <label className={`mw-type ${formData.type === 1 ? 'is-checked' : ''}`}>
          <input
            type="radio"
            name="type"
            value="1"
            checked={formData.type === 1}
            onChange={(e) => setFormData({ ...formData, type: parseInt(e.target.value, 10) })}
          />
          <div className="mw-type-name">Single</div>
          <div className="mw-type-price">
            ${pricing?.single?.priceUsd || 150} USD / {pricing?.single?.duration || 12} months
          </div>
        </label>
        <label className={`mw-type ${formData.type === 2 ? 'is-checked' : ''}`}>
          <input
            type="radio"
            name="type"
            value="2"
            checked={formData.type === 2}
            onChange={(e) => setFormData({ ...formData, type: parseInt(e.target.value, 10) })}
          />
          <div className="mw-type-name">Couple</div>
          <div className="mw-type-price">
            ${pricing?.couple?.priceUsd || 250} USD / {pricing?.couple?.duration || 12} months
          </div>
        </label>
      </div>
    </section>
  );

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="mw-form" noValidate>
      <h2>Apply for Membership</h2>
      <p className="mw-lead">Join the inner circle and unlock priority access.</p>

      {error && (
        <div className="mw-alert mw-alert-danger">
          <strong>Please fix the following:</strong>
          <div>{error}</div>
        </div>
      )}

      <section>
        <h3>Personal Information</h3>
        <div className="mw-grid">
          <div className="mw-field" id="mw-field-name">
            <label className="mw-required">Full Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              onBlur={(e) => validateSingle(e.target.name)}
              className={fieldErrors.name ? 'mw-invalid' : ''}
              required
            />
            {fieldErrors.name && <div className="mw-invalid-feedback">{fieldErrors.name}</div>}
          </div>
          <div className="mw-field" id="mw-field-email">
            <label className="mw-required">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={(e) => validateSingle(e.target.name)}
              className={fieldErrors.email ? 'mw-invalid' : ''}
              required
            />
            {fieldErrors.email && <div className="mw-invalid-feedback">{fieldErrors.email}</div>}
          </div>
          <div className={`mw-field ${fieldErrors.phone ? 'mw-has-error' : ''}`} id="mw-field-phone">
            <label className="mw-required">Phone Number</label>
            <PhoneInput
              country={'ae'}
              value={formData.phone}
              onChange={(v) => handlePhoneChange(v, 'phone')}
              onBlur={() => validateSingle('phone')}
              inputClass="mw-phone-input"
              containerClass="mw-phone-container"
            />
            {fieldErrors.phone && <div className="mw-invalid-feedback">{fieldErrors.phone}</div>}
          </div>
          <div className="mw-field" id="mw-field-socialMediaLink">
            <label className="mw-required">Social Media Link</label>
            <input
              type="text"
              name="socialMediaLink"
              value={formData.socialMediaLink}
              onChange={handleChange}
              onBlur={(e) => validateSingle(e.target.name)}
              placeholder="https://instagram.com/yourhandle"
              className={fieldErrors.socialMediaLink ? 'mw-invalid' : ''}
            />
            {fieldErrors.socialMediaLink && (
              <div className="mw-invalid-feedback">{fieldErrors.socialMediaLink}</div>
            )}
          </div>
          <div className="mw-field" id="mw-field-birthdate">
            <label>Birthdate</label>
            <input
              type="date"
              name="birthdate"
              value={formData.birthdate}
              onChange={handleChange}
              onBlur={(e) => validateSingle(e.target.name)}
              max={maxBirthdate()}
              className={fieldErrors.birthdate ? 'mw-invalid' : ''}
            />
            {fieldErrors.birthdate && (
              <div className="mw-invalid-feedback">{fieldErrors.birthdate}</div>
            )}
          </div>
          <div className="mw-field">
            <label>Gender</label>
            <select name="gender" value={formData.gender} onChange={handleChange}>
              <option value="1">Male</option>
              <option value="2">Female</option>
            </select>
          </div>
          <div className="mw-field mw-field-full" id="mw-field-photo">
            <label className="mw-required">Your Photo</label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => handlePhotoChange(e, 'photo')}
              className={fieldErrors.photo ? 'mw-invalid' : ''}
            />
            <small className="mw-help-text">JPEG, PNG or WebP. Max 5 MB.</small>
            {photoPreview && (
              <div className="mw-photo-preview">
                <img
                  src={photoPreview}
                  alt="Your photo preview"
                  style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8 }}
                />
                <button
                  type="button"
                  className="mw-btn mw-btn-outline"
                  onClick={() => handleRemovePhoto('photo')}
                >
                  Remove
                </button>
              </div>
            )}
            {fieldErrors.photo && <div className="mw-invalid-feedback">{fieldErrors.photo}</div>}
          </div>
        </div>
      </section>

      {formData.type === 2 && (
        <section>
          <h3>Partner Information</h3>
          <div className="mw-grid">
            <div className="mw-field" id="mw-field-coupleName">
              <label className="mw-required">Partner's Full Name</label>
              <input
                type="text"
                name="coupleName"
                value={formData.coupleName}
                onChange={handleChange}
                onBlur={(e) => validateSingle(e.target.name)}
                className={fieldErrors.coupleName ? 'mw-invalid' : ''}
                required
              />
              {fieldErrors.coupleName && (
                <div className="mw-invalid-feedback">{fieldErrors.coupleName}</div>
              )}
            </div>
            <div className={`mw-field ${fieldErrors.couplePhone ? 'mw-has-error' : ''}`} id="mw-field-couplePhone">
              <label className="mw-required">Partner's Phone</label>
              <PhoneInput
                country={'ae'}
                value={formData.couplePhone}
                onChange={(v) => handlePhoneChange(v, 'couplePhone')}
                onBlur={() => validateSingle('couplePhone')}
                inputClass="mw-phone-input"
                containerClass="mw-phone-container"
              />
              {fieldErrors.couplePhone && (
                <div className="mw-invalid-feedback">{fieldErrors.couplePhone}</div>
              )}
            </div>
            <div className="mw-field" id="mw-field-coupleEmail">
              <label className="mw-required">Partner's Email</label>
              <input
                type="email"
                name="coupleEmail"
                value={formData.coupleEmail}
                onChange={handleChange}
                onBlur={(e) => validateSingle(e.target.name)}
                className={fieldErrors.coupleEmail ? 'mw-invalid' : ''}
                required
              />
              {fieldErrors.coupleEmail && (
                <div className="mw-invalid-feedback">{fieldErrors.coupleEmail}</div>
              )}
            </div>
            <div className="mw-field" id="mw-field-coupleSocialMediaLink">
              <label className="mw-required">Partner's Social Media</label>
              <input
                type="text"
                name="coupleSocialMediaLink"
                value={formData.coupleSocialMediaLink}
                onChange={handleChange}
                onBlur={(e) => validateSingle(e.target.name)}
                placeholder="https://instagram.com/partnerhandle"
                className={fieldErrors.coupleSocialMediaLink ? 'mw-invalid' : ''}
              />
              {fieldErrors.coupleSocialMediaLink && (
                <div className="mw-invalid-feedback">{fieldErrors.coupleSocialMediaLink}</div>
              )}
            </div>
            <div className="mw-field mw-field-full" id="mw-field-couplePhoto">
              <label className="mw-required">Partner's Photo</label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => handlePhotoChange(e, 'couplePhoto')}
                className={fieldErrors.couplePhoto ? 'mw-invalid' : ''}
              />
              <small className="mw-help-text">JPEG, PNG or WebP. Max 5 MB.</small>
              {couplePhotoPreview && (
                <div className="mw-photo-preview">
                  <img
                    src={couplePhotoPreview}
                    alt="Partner's photo preview"
                    style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8 }}
                  />
                  <button
                    type="button"
                    className="mw-btn mw-btn-outline"
                    onClick={() => handleRemovePhoto('couplePhoto')}
                  >
                    Remove
                  </button>
                </div>
              )}
              {fieldErrors.couplePhoto && (
                <div className="mw-invalid-feedback">{fieldErrors.couplePhoto}</div>
              )}
            </div>
          </div>
        </section>
      )}

      <section>
        <h3>Address Information</h3>
        <div className="mw-grid">
          <div className="mw-field">
            <label>Street Address</label>
            <input type="text" name="addressStreet" value={formData.addressStreet} onChange={handleChange} />
          </div>
          <div className="mw-field">
            <label>Neighborhood</label>
            <input
              type="text"
              name="addressNeighborhood"
              value={formData.addressNeighborhood}
              onChange={handleChange}
            />
          </div>
          <div className="mw-field">
            <label>Country of Residence</label>
            <input
              type="text"
              name="residenceCountry"
              value={formData.residenceCountry}
              onChange={handleChange}
            />
          </div>
          <div className="mw-field">
            <label>Additional Info</label>
            <input
              type="text"
              name="addressMoreInfo"
              value={formData.addressMoreInfo}
              onChange={handleChange}
              placeholder="Apartment, floor, etc."
            />
          </div>
        </div>
      </section>

      <section>
        <h3>What Happens Next</h3>
        <ol className="mw-steps">
          <li className="mw-step mw-step-current">
            <span className="mw-step-num">1</span>
            <div className="mw-step-body">
              <div className="mw-step-title">Submit your application</div>
              <div className="mw-step-desc">Fill in your details and continue to payment.</div>
            </div>
          </li>
          <li className="mw-step">
            <span className="mw-step-num">2</span>
            <div className="mw-step-body">
              <div className="mw-step-title">Authorize payment</div>
              <div className="mw-step-desc">Securely authorize your card via Paymob — no charge yet.</div>
            </div>
          </li>
          <li className="mw-step">
            <span className="mw-step-num">3</span>
            <div className="mw-step-body">
              <div className="mw-step-title">Admin review</div>
              <div className="mw-step-desc">Our team reviews and approves your application.</div>
            </div>
          </li>
          <li className="mw-step">
            <span className="mw-step-num">4</span>
            <div className="mw-step-body">
              <div className="mw-step-title">Membership activated</div>
              <div className="mw-step-desc">Your card is charged and your benefits begin.</div>
            </div>
          </li>
        </ol>
      </section>

      <div className="mw-alert mw-alert-info">
        <strong>Next: Payment Authorization.</strong>
        <p>After submitting you'll be redirected to securely authorize your payment. Your card is only charged once the application is approved.</p>
      </div>

      <div className="mw-actions">
        <button type="submit" className="mw-btn" disabled={submitting}>
          {submitting ? 'Processing…' : 'Continue to Payment'}
        </button>
      </div>
    </form>
  );

  // ---------- early states ----------
  if (authExpired) {
    return (
      <div className="membership-widget-wrapper" style={themeStyles}>
        <div className="mw-alert mw-alert-warning" style={{ maxWidth: 520, margin: '2rem auto', textAlign: 'center' }}>
          <strong>Session expired.</strong>
          <p>Please sign in again to continue with your membership application.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="membership-widget-wrapper" style={themeStyles}>
        <div className="mw-loading">
          <div className="mw-spinner" aria-hidden="true" />
          <span>Loading membership…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="membership-widget-wrapper" style={themeStyles}>
      <div className="mw-inner">
        <header className="mw-header">
          <h1>Membership</h1>
          <p className="mw-lead">Join our family and enjoy exclusive benefits</p>
        </header>

        {showForm && renderTypeSelector()}

        {renderInfoSections()}

        {membershipStatus?.hasMembership && (
          <>
            {renderMembershipCard()}
            {renderStatusMessage()}
          </>
        )}
        {!membershipStatus?.hasMembership && renderStatusMessage()}
        {showForm && renderForm()}
        {!membershipStatus?.hasMembership && !membershipStatus?.canApply && !showForm && (
          <div className="mw-alert mw-alert-info">
            You cannot apply for membership at this time.
          </div>
        )}
      </div>
    </div>
  );
};

export default MembershipWidget;
